import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../Atoms/Input/Input";
import ContactInput from "../../Atoms/Input/ContactInput";
import Button from "../button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import PatientListService from "../../../services/PatientListService";
import { eventEHRCustomToastMessage } from "../../Atoms/custom-toast-message/EventEHRCustomToastMessage";
import { UnitNumberRecord } from "./UnitNumberSelect";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "520px",
    maxWidth: "none",
  },
}));

const FIELD = {
  UNIT_NUMBER: "unitNumber",
  LEADER_NAME: "leaderName",
  EMAIL: "email",
  LEADER_CONTACT: "leaderContact",
};

interface AddUnitNumberDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  onCreated: (record: UnitNumberRecord) => void;
  defaultUnitNumber?: string;
}

export default function AddUnitNumberDialog({
  isOpen,
  handleClose,
  onCreated,
  defaultUnitNumber = "",
}: AddUnitNumberDialogProps) {
  const validationSchema = Yup.object().shape({
    [FIELD.UNIT_NUMBER]: Yup.string().required("Unit number is required"),
    [FIELD.LEADER_NAME]: Yup.string().matches(VALIDATION_CONSTANTS.CHARACTER, {
      message: "Leader name can only contain characters",
      excludeEmptyString: true,
    }),
    [FIELD.EMAIL]: Yup.string().matches(VALIDATION_CONSTANTS.EMAIL, {
      message: "Enter a valid email address",
      excludeEmptyString: true,
    }),
    [FIELD.LEADER_CONTACT]: Yup.string().matches(
      VALIDATION_CONSTANTS.CONTACT_NUMBER,
      { message: "Invalid contact number", excludeEmptyString: true },
    ),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const payload = {
        unitNumber: values[FIELD.UNIT_NUMBER]?.trim(),
        leaderName: values[FIELD.LEADER_NAME]?.trim() || undefined,
        email: values[FIELD.EMAIL]?.trim() || undefined,
        leaderContact: values[FIELD.LEADER_CONTACT] || undefined,
      };
      const res = await PatientListService.createBaseCamp(payload);
      const created =
        res?.data?.basecamp ?? res?.data?.data ?? res?.data ?? {};
      const record: UnitNumberRecord = {
        id: created?.id,
        unitNumber: created?.unitNumber || payload.unitNumber,
        leaderName: created?.leaderName ?? payload.leaderName,
        email: created?.email ?? payload.email,
        leaderContact: created?.leaderContact ?? payload.leaderContact,
      };
      eventEHRCustomToastMessage({
        status: "success",
        title: res?.data?.message || "Unit number created",
      });
      onCreated(record);
    } catch (error: any) {
      eventEHRCustomToastMessage({
        status: "error",
        title:
          error?.response?.data?.message ||
          "Failed to create unit number. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="add-unit-number-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [FIELD.UNIT_NUMBER]: defaultUnitNumber,
          [FIELD.LEADER_NAME]: "",
          [FIELD.EMAIL]: "",
          [FIELD.LEADER_CONTACT]: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="add-unit-number-dialog-title"
              >
                Add New Unit Number
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <Input
                    textFieldLabel="Unit Number"
                    size="small"
                    name={FIELD.UNIT_NUMBER}
                    value={values[FIELD.UNIT_NUMBER]}
                    onChangeCb={(e) =>
                      setFieldValue(FIELD.UNIT_NUMBER, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[FIELD.UNIT_NUMBER] &&
                      Boolean(errors[FIELD.UNIT_NUMBER])
                    }
                    helperText={
                      touched[FIELD.UNIT_NUMBER]
                        ? (errors[FIELD.UNIT_NUMBER] as string)
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Leader Name"
                    size="small"
                    name={FIELD.LEADER_NAME}
                    value={values[FIELD.LEADER_NAME]}
                    onChangeCb={(e) =>
                      setFieldValue(FIELD.LEADER_NAME, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[FIELD.LEADER_NAME] &&
                      Boolean(errors[FIELD.LEADER_NAME])
                    }
                    helperText={
                      touched[FIELD.LEADER_NAME]
                        ? (errors[FIELD.LEADER_NAME] as string)
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Email"
                    size="small"
                    name={FIELD.EMAIL}
                    value={values[FIELD.EMAIL]}
                    onChangeCb={(e) => setFieldValue(FIELD.EMAIL, e.target.value)}
                    onBlur={handleBlur}
                    error={touched[FIELD.EMAIL] && Boolean(errors[FIELD.EMAIL])}
                    helperText={
                      touched[FIELD.EMAIL]
                        ? (errors[FIELD.EMAIL] as string)
                        : undefined
                    }
                  />
                  <ContactInput
                    textFieldLabel="Leader Contact Number"
                    name={FIELD.LEADER_CONTACT}
                    value={values[FIELD.LEADER_CONTACT]}
                    onChangeCb={(value: any) =>
                      setFieldValue(FIELD.LEADER_CONTACT, value)
                    }
                    onBlur={handleBlur}
                    size="small"
                    isContactNumber={true}
                    error={
                      touched[FIELD.LEADER_CONTACT] &&
                      Boolean(errors[FIELD.LEADER_CONTACT])
                    }
                    helperText={
                      touched[FIELD.LEADER_CONTACT]
                        ? (errors[FIELD.LEADER_CONTACT] as string)
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "160px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
